import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Tutorial } from './tutorial.service';

/**
 * State object for the tutorial modal
 */
export interface TutorialModalState {
  tutorial: Tutorial | null;
  boardId: string | null;
  userId: string | null;
  isVisible: boolean;
  userIsAdmin: boolean;
}

/**
 * Service for managing tutorial modal visibility and state across the application.
 * 
 * Provides a centralized way to show tutorials from anywhere in the app.
 * Components can subscribe to state changes to react to modal visibility.
 */
@Injectable({
  providedIn: 'root'
})
export class TutorialModalService {
  
  /** BehaviorSubject that holds the current modal state */
  private state = new BehaviorSubject<TutorialModalState>({
    tutorial: null,
    boardId: null,
    userId: null,
    isVisible: false,
    userIsAdmin: false
  });

  /** Observable of modal state changes - components can subscribe to this */
  public state$: Observable<TutorialModalState> = this.state.asObservable();

  constructor() { }

  /**
   * Show a tutorial in the modal
   * 
   * @param tutorial - The tutorial to display
   * @param boardId - The current board ID
   * @param userId - The current user ID
   * @param userIsAdmin - Whether the current user has admin permissions
   */
  showTutorial(tutorial: Tutorial, boardId: string, userId: string, userIsAdmin: boolean = false): void {
    this.state.next({
      tutorial,
      boardId,
      userId,
      isVisible: true,
      userIsAdmin
    });
  }

  /**
   * Hide the tutorial modal
   */
  hideTutorial(): void {
    this.state.next({
      ...this.state.value,
      isVisible: false
    });
  }

  /**
   * Get the current state value (synchronous access)
   */
  getState(): TutorialModalState {
    return this.state.value;
  }
}
